<template>
  <div class="stepper-row">
    <div class="stepper-row__prev">
      <v-btn
        v-if="currentStep > 1"
        class="stepper-nav-btn stepper-nav-btn--prev"
        outlined
        @click="$emit('prev')"
      >
        Previous
      </v-btn>
    </div>
    <div class="stepper-header">
      <div
        v-for="(step, idx) in steps"
        :key="step.step"
        class="stepper-header__segment"
        :class="{
          'stepper-header__segment--active': currentStep === step.step,
          'stepper-header__segment--complete': currentStep > step.step && isStepClickable(step.step),
          'stepper-header__segment--clickable': isStepClickable(step.step),
          'stepper-header__segment--locked': !isStepClickable(step.step),
        }"
        @click="isStepClickable(step.step) && $emit('update:currentStep', step.step)"
      >
        <div class="stepper-header__row">
          <div
            v-if="idx > 0"
            class="stepper-header__connector"
            :class="{ 'stepper-header__connector--filled': currentStep >= step.step }"
          />
          <div class="stepper-header__step">
            <span v-if="isStepLocked(step.step)" class="stepper-header__lock">
              <v-icon small>mdi-lock</v-icon>
            </span>
            <span v-else-if="currentStep > step.step" class="stepper-header__check">✓</span>
            <span v-else class="stepper-header__num">{{ step.step }}</span>
          </div>
          <div
            v-if="idx < steps.length - 1"
            class="stepper-header__connector"
            :class="{ 'stepper-header__connector--filled': currentStep > step.step }"
          />
        </div>
        <span class="stepper-header__label">{{ step.label }}</span>
      </div>
    </div>
    <div class="stepper-row__next">
      <v-btn
        color="primary"
        class="stepper-nav-btn stepper-nav-btn--next"
        :disabled="isNextDisabled"
        @click="$emit('next')"
      >
        {{ currentStep === totalSteps ? 'Done' : 'Next' }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { STEPS } from './constants'

export default {
  name: 'StepperHeader',
  props: {
    currentStep: { type: Number, required: true },
    totalSteps: { type: Number, default: 3 },
    maxStepReached: { type: Number, required: true },
    isNextDisabled: { type: Boolean, default: false },
    steps: { type: Array, default: () => STEPS },
    /** When true, steps 2 and 3 are clickable; when false, they show lock and are not clickable. */
    hasEditsGenerated: { type: Boolean, default: false },
  },
  methods: {
    isStepClickable(stepNum) {
      if (stepNum === 1) return true
      return this.hasEditsGenerated
    },
    isStepLocked(stepNum) {
      if (stepNum === 1) return false
      return !this.hasEditsGenerated
    },
  },
}
</script>

<style scoped>
.stepper-row {
  display: flex;
  align-items: flex-start;
  gap: 32px;
  padding: 16px 28px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(0, 0, 0, 0.06);
}

.stepper-row__prev {
  flex-shrink: 0;
  width: 112px;
  min-width: 112px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stepper-row__next {
  flex-shrink: 0;
  min-width: 112px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.stepper-header {
  display: flex;
  align-items: flex-start;
  justify-content: stretch;
  flex: 1;
  min-width: 0;
  padding: 0;
}

.stepper-header__segment {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
  cursor: default;
}

.stepper-header__segment--complete,
.stepper-header__segment--clickable {
  cursor: pointer;
}

.stepper-header__segment--locked {
  cursor: not-allowed;
  opacity: 0.85;
}

.stepper-header__segment--locked .stepper-header__step {
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.35);
  border-color: rgba(255, 255, 255, 0.08);
}

.stepper-header__lock {
  display: flex;
  align-items: center;
  justify-content: center;
}

.stepper-header__row {
  display: flex;
  align-items: center;
  width: 100%;
}

.stepper-header__connector {
  flex: 1;
  height: 4px;
  min-width: 20px;
  margin: 0 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  transition: background 0.25s ease;
}

.stepper-header__connector--filled {
  background: linear-gradient(90deg, #64b5f6, #42a5f5);
  box-shadow: 0 0 12px rgba(66, 165, 245, 0.35);
}

.stepper-header__step {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  transition: all 0.25s ease;
  flex-shrink: 0;
}

.stepper-header__segment--active .stepper-header__step {
  background: linear-gradient(145deg, #5eb0f7, #3d9ae8);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: 0 4px 20px rgba(66, 165, 245, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.stepper-header__segment--complete .stepper-header__step {
  background: linear-gradient(145deg, #5eb0f7, #3d9ae8);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 2px 12px rgba(66, 165, 245, 0.3);
}

.stepper-header__check {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
}

.stepper-header__label {
  margin-top: 12px;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  max-width: 100px;
}

.stepper-header__segment--active .stepper-header__label {
  color: rgba(255, 255, 255, 0.98);
}

.stepper-header__segment--complete .stepper-header__label {
  color: rgba(255, 255, 255, 0.7);
}

.stepper-nav-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  width: 100%;
  min-width: 0;
  max-width: 112px;
}

.stepper-nav-btn--prev {
  border-color: rgba(255, 255, 255, 0.35) !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

.stepper-nav-btn--prev:hover {
  border-color: #64b5f6 !important;
  color: #64b5f6 !important;
  background: rgba(66, 165, 245, 0.08) !important;
}

.stepper-nav-btn--next {
  box-shadow: 0 2px 12px rgba(66, 165, 245, 0.35);
}
</style>
